/**
 * Section state machine for the LLD Practice workspace.
 * Manages section lock/active/completed states based on mode and progress.
 * Pure functions with no side effects.
 */

export type Mode = 'learn' | 'practice' | 'interview';

export type SectionId =
  | 'problem'
  | 'requirements'
  | 'entities'
  | 'responsibilities'
  | 'relationships'
  | 'uml'
  | 'implementation'
  | 'testing';

export type SectionStatus = 'locked' | 'active' | 'completed';

/**
 * The fixed sequence of sections in the LLD workspace.
 */
export const SECTION_SEQUENCE: SectionId[] = [
  'problem',
  'requirements',
  'entities',
  'responsibilities',
  'relationships',
  'uml',
  'implementation',
];

/**
 * Workspace data used to determine section completion.
 */
export interface WorkspaceData {
  entities: { id: string; name: string }[];
  relationships: { id: string }[];
  umlClasses: { id: string }[];
  codeFiles: { path: string; content: string }[];
  testsExecuted: number;
  requirementsCount?: number;
}

/**
 * Per-section completion state, true if that section has been completed.
 */
export type SectionProgress = Record<SectionId, boolean>;

/**
 * Returns the status (locked/active/completed) for each section based on mode and progress.
 *
 * In Learn Mode:
 * - Completed sections are those marked true in sectionProgress
 * - The active section is the first section in sequence that is not completed
 * - All sections after the active section are locked
 *
 * In Practice/Interview Mode:
 * - Completed sections are those marked true in sectionProgress
 * - All non-completed sections are active (freely navigable)
 *
 * @param mode - Current workspace mode
 * @param sectionProgress - Completion state for each section
 * @returns A record mapping each section to its status
 */
export function getSectionStates(
  mode: Mode,
  sectionProgress: SectionProgress
): Record<SectionId, SectionStatus> {
  const states: Record<SectionId, SectionStatus> = {} as Record<SectionId, SectionStatus>;

  if (mode === 'learn') {
    let foundActive = false;

    for (const section of SECTION_SEQUENCE) {
      if (sectionProgress[section]) {
        states[section] = 'completed';
      } else if (!foundActive) {
        states[section] = 'active';
        foundActive = true;
      } else {
        states[section] = 'locked';
      }
    }
  } else {
    // Practice and Interview modes: all sections accessible
    for (const section of SECTION_SEQUENCE) {
      if (sectionProgress[section]) {
        states[section] = 'completed';
      } else {
        states[section] = 'active';
      }
    }
  }

  // Testing is intentionally parked for a later release. Keep its state key
  // for persisted-data compatibility, but never expose it as an active step.
  states.testing = 'locked';

  return states;
}

/**
 * Returns the recommended section in Practice Mode — the first incomplete section
 * in the fixed sequence.
 *
 * @param sectionProgress - Completion state for each section
 * @returns The first incomplete section, or null if all are completed
 */
export function getRecommendedSection(
  sectionProgress: SectionProgress
): SectionId | null {
  for (const section of SECTION_SEQUENCE) {
    if (!sectionProgress[section]) {
      return section;
    }
  }
  return null;
}

/**
 * Determines if a section is complete based on workspace data and per-section thresholds.
 *
 * Thresholds:
 * - problem: always considered complete (read-only content)
 * - requirements: at least 1 requirement captured
 * - entities: at least 1 entity
 * - responsibilities: at least 1 entity (responsibilities are mapped to entities)
 * - relationships: at least 1 relationship
 * - uml: at least 1 class in UML diagram
 * - implementation: at least 1 file with non-empty code
 * - testing: at least 1 test executed
 *
 * @param section - The section to check
 * @param workspaceData - Current workspace data
 * @returns true if the section meets its completion threshold
 */
export function isSectionComplete(
  section: SectionId,
  workspaceData: WorkspaceData
): boolean {
  switch (section) {
    case 'problem':
      return true;
    case 'requirements':
      return (workspaceData.requirementsCount ?? 0) >= 1;
    case 'entities':
      return workspaceData.entities.length >= 1;
    case 'responsibilities':
      return workspaceData.entities.length >= 1;
    case 'relationships':
      return workspaceData.relationships.length >= 1;
    case 'uml':
      return workspaceData.umlClasses.length >= 1;
    case 'implementation':
      return workspaceData.codeFiles.some((file) => file.content.trim().length > 0);
    case 'testing':
      return workspaceData.testsExecuted >= 1;
    default:
      return false;
  }
}
